<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
const { t } = useI18n();
const { sessionContext } = useSessionContext();
const { countryConfig, deleteCart } = useCountrySwitch();
const country = useState<AvailableCountries>('country');
const modalController = useModal();
const { currency } = useSessionContext();
const route = useRoute();

const countryCookie = useCookie('country', { maxAge: 92275200 });
const showCountrySwitchInfo = useCookie<boolean>('showCountrySwitchInfo', { default: () => false, maxAge: 92275200 });
const customerStore = useCustomerStore();
const { signedIn } = storeToRefs(customerStore);

// CUSTOM IDS-61: Add props to display country switch on both footer and header
const props = withDefaults(
    defineProps<{
        variant?: 'header' | 'footer';
    }>(),
    {
        variant: 'footer',
    },
);
if (!countryCookie.value && props.variant === 'footer') modalController.open();

const isPageRefreshed = () => {
    if (performance.getEntriesByType) {
        const entries = performance.getEntriesByType('navigation');
        return entries.length > 0 && (entries[0] as PerformanceNavigationTiming).type === 'reload';
    }
    return window.performance?.navigation?.type === 1; // Fallback for older browsers
};

const selectedCountry = ref(country.value);

const countryArray: Country[] = ref(
    Object.keys(countryConfig).map(countryKey => ({
        ...countryConfig[countryKey],
        label: t(countryConfig[countryKey].translation),
        value: countryKey,
    })),
);

const onCountryChange = async () => {
    if (selectedCountry.value !== country.value) {
        countryCookie.value = selectedCountry.value;
        showCountrySwitchInfo.value = true;

        await deleteCart(sessionContext.value.token);
        window.location.replace(`${countryConfig[selectedCountry.value].prefix}/`);
    }
};

const selectPreferredCountry = (country: Country) => {
    if (selectedCountry.value === country.value) {
        modalController.close();
        countryCookie.value = country.value;
        return;
    }

    selectedCountry.value = country.value;
    onCountryChange();
};

// CUSTOM IDS-61: Check if user can close the modal. return country translated name
const isModalClosable = computed(() => !!countryCookie.value);
const currentCountry = computed(
    () => `${t(countryConfig[selectedCountry.value].translation)} (${currency?.value?.symbol})`,
);

watch(countryCookie, (oldValue, newValue) => {
    if (oldValue !== newValue) {
        showCountrySwitchInfo.value = true;
    }
});
watch(
    () => route.fullPath,
    () => {
        showCountrySwitchInfo.value = false;
    },
);

onMounted(() => {
    if (isPageRefreshed()) {
        showCountrySwitchInfo.value = false; // Clear the cookie if the user refreshed manually
    }
});
</script>

<template>
    
<FormKitLazyProvider config-file="true">
<LazySharedModal
        :controller="modalController"
        :closable="isModalClosable"
        :with-close-button="isModalClosable"
        :with-trigger="false"
    >
        <!--CUSTOM IDS-61: Changed styling of the modal, added subtitle-->
        <template #title>
            <div>
                <h2 class="font-main text-3xl leading-8.5">{{ $t('global.countrySelect.title') }}</h2>
                <p class="font-main text-base font-normal leading-6">
                    {{ countryCookie ? $t('global.countrySelect.subTitleV2') : $t('global.countrySelect.subTitle') }}
                </p>
            </div>
        </template>
        <template #content>
            <div class="space-y-3">
                <div
                    v-for="availableCountry in countryArray"
                    :key="availableCountry.value"
                    class="flex cursor-pointer items-center justify-between border border-neutral-300 bg-neutral-400 p-5 font-main text-lg font-extrabold leading-5.5 hover:bg-neutral-300"
                    @click="selectPreferredCountry(availableCountry)"
                >
                    <span>
                        {{ availableCountry.label }}
                    </span>

                    <FormKitIcon
                        icon="ids_arrow-right"
                        class="block h-5 w-5"
                    />
                </div>
            </div>
        </template>
    </LazySharedModal>
    <ClientOnly>
        <div v-if="variant === 'footer'">
            <span class="text-md font-normal leading-6">{{ $t('global.header.countrySwitch.countryInfo') }}</span>
            <span class="mr-2 text-md font-bold leading-6"> {{ currentCountry }}</span>

            <button
                class="text-base font-normal leading-6.5 underline"
                @click="modalController.open"
            >
                {{ $t('global.header.countrySwitch.changeCountry') }}
            </button>
        </div>
        <div
            v-if="variant === 'header' && countryCookie && showCountrySwitchInfo && !signedIn"
            class="container mt-6"
        >
            <div class="flex min-h-14 w-full items-center bg-accent-blue px-4 py-3.75">
                <FormKitIcon
                    icon="info"
                    class="text-white block h-5 w-5 shrink-0 rounded-full bg-accent-blue-dark p-1.5"
                />
                <span class="ml-2">
                    {{ $t('global.header.countrySwitch.headerMessage', { country: currentCountry }) }}
                    <button
                        class="underline"
                        @click="modalController.open"
                    >
                        {{ $t('global.header.countrySwitch.endHeaderMessage') }}
                    </button>
                </span>
            </div>
        </div>
    </ClientOnly>
</FormKitLazyProvider>

</template>
